import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CreateInstallmentPlanPayload,
  InstallmentPaymentRecord,
  InstallmentPlanRecord,
  InstallmentScheduleRecord,
  SalesApiService
} from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-installment-plan-detail',
  imports: [
    AinAmountPipe, AinDatePipe, AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule, InputTextModule,
    InputNumberModule, SelectModule, TableModule, DialogModule, CustomerSelectorComponent,
    CurrencySelectorComponent, JalaliDateInputComponent
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('sales.installments.form.createTitle') : t('sales.installments.form.editTitle')" [description]="t('sales.installments.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <header class="card">
          <div class="toolbar">
            <a class="back-link" [routerLink]="['/sales/installment-plans']">{{ t('catalog.action.backToList') }}</a>
            @if (isCreateMode()) { <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button> }
          </div>
        </header>
        @if (isCreateMode()) {
          <section class="card grid">
            <label><span>{{ t('sales.installments.fields.planNumber') }}</span><input pInputText [(ngModel)]="form.planNumber" /></label>
            <label><span>{{ t('sales.orders.fields.customer') }}</span><ain-customer-selector [(ngModel)]="form.customerAccountId" /></label>
            <label><span>{{ t('sales.installments.fields.principalAmount') }}</span><p-inputNumber [(ngModel)]="form.totalAmount" [min]="0" /></label>
            <label><span>{{ t('sales.installments.fields.currency') }}</span><ain-currency-selector [(ngModel)]="form.currencyCode" /></label>
            <label><span>{{ t('sales.installments.fields.installmentCount') }}</span><p-inputNumber [(ngModel)]="form.installmentCount" [min]="1" /></label>
            <label>
              <span>{{ t('sales.installments.fields.frequency') }}</span>
              <p-select [options]="frequencyOptions" optionValue="value" [(ngModel)]="form.frequency" appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label><span>{{ t('sales.installments.fields.firstDue') }}</span><ain-date-input [(ngModel)]="form.firstDueDateUtc" /></label>
            <label class="check"><input type="checkbox" [(ngModel)]="form.hasInterest" /><span>{{ t('sales.installments.fields.hasInterest') }}</span></label>
            @if (form.hasInterest) {
              <label><span>{{ t('sales.installments.fields.feeRate') }}</span><p-inputNumber [(ngModel)]="form.feeRate" [min]="0" [max]="100" suffix="%" /></label>
              <label>
                <span>{{ t('sales.installments.fields.feePeriod') }}</span>
                <p-select [options]="feePeriodOptions" optionValue="value" [(ngModel)]="form.feePeriod" appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
            }
            @if (previewTotalAmount() > 0) {
              <p class="preview full">{{ t('sales.installments.fields.totalPayable') }}: <strong>{{ previewTotalAmount() | ainAmount }} {{ form.currencyCode }}</strong></p>
            }
          </section>
        } @else {
          @if (plan()) {
            <section class="card summary">
              <article><span>{{ t('sales.installments.fields.planNumber') }}</span><strong>{{ plan()!.planNumber }}</strong></article>
              <article><span>{{ t('sales.installments.fields.principalAmount') }}</span><strong>{{ planPrincipalAmount() | ainAmount }} {{ plan()!.currencyCode }}</strong></article>
              <article><span>{{ t('sales.installments.fields.totalPayable') }}</span><strong>{{ plan()!.totalAmount | ainAmount }} {{ plan()!.currencyCode }}</strong></article>
              <article><span>{{ t('sales.installments.fields.totalInterest') }}</span><strong>{{ totalInterest() | ainAmount }} {{ plan()!.currencyCode }}</strong></article>
              <article><span>{{ t('sales.installments.fields.totalPaid') }}</span><strong>{{ totalPaid() | ainAmount }} {{ plan()!.currencyCode }}</strong></article>
              <article><span>{{ t('sales.installments.fields.totalRemaining') }}</span><strong>{{ totalRemaining() | ainAmount }} {{ plan()!.currencyCode }}</strong></article>
              <article><span>{{ t('sales.installments.fields.overdueAmount') }}</span><strong class="overdue">{{ overdueAmount() | ainAmount }} {{ plan()!.currencyCode }}</strong></article>
              <article><span>{{ t('sales.installments.fields.hasInterest') }}</span><strong>{{ plan()!.hasInterest ? t('common.yes') : t('common.no') }}</strong></article>
              @if (plan()!.hasInterest) {
                <article><span>{{ t('sales.installments.fields.feeRate') }}</span><strong>{{ plan()!.feeRate }}%</strong></article>
                <article><span>{{ t('sales.installments.fields.feePeriod') }}</span><strong>{{ feePeriodLabel(plan()!.feePeriod) }}</strong></article>
              }
              <article><span>{{ t('columns.status') }}</span><strong>{{ statusLabel(plan()!.status) }}</strong></article>
            </section>
          }
          <div class="tabs">
            <button type="button" [class.active]="activeTab()==='schedules'" (click)="setTab('schedules')">{{ t('sales.installments.tabs.schedules') }}</button>
            <button type="button" [class.active]="activeTab()==='payments'" (click)="setTab('payments')">{{ t('sales.installments.tabs.payments') }}</button>
          </div>
          @if (activeTab()==='schedules') {
            <p-table [value]="schedules()" [paginator]="true" [rows]="12">
              <ng-template pTemplate="header">
                <tr>
                  <th>#</th><th>{{ t('sales.installments.fields.dueDate') }}</th><th>{{ t('sales.installments.fields.scheduledAmount') }}</th>
                  <th>{{ t('sales.installments.fields.paidAmount') }}</th><th>{{ t('columns.status') }}</th><th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.sequenceNumber }}</td>
                  <td>{{ row.dueDateUtc | ainDate }}</td>
                  <td>{{ row.scheduledAmount | ainAmount }}</td>
                  <td>{{ row.paidAmount | ainAmount }}</td>
                  <td>{{ statusLabel(row.status) }}</td>
                  <td>@if (row.remainingAmount > 0) { <button pButton type="button" size="small" [label]="t('sales.installments.pay')" (click)="openPay(row)"></button> }</td>
                </tr>
              </ng-template>
            </p-table>
          }
          @if (activeTab()==='payments') {
            <p-table [value]="payments()" [paginator]="true" [rows]="12">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('sales.installments.fields.installmentRef') }}</th>
                  <th>{{ t('sales.installments.fields.paidAt') }}</th>
                  <th>{{ t('sales.installments.fields.amount') }}</th>
                  <th>{{ t('sales.credit.fields.notes') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>#{{ installmentSequence(row.installmentScheduleId) }}</td>
                  <td>{{ row.paidAtUtc | ainDate:'datetime' }}</td>
                  <td>{{ row.amount | ainAmount }} {{ plan()?.currencyCode }}</td>
                  <td>{{ row.notes || '-' }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        }
        @if (errorMessage()) { <p class="error">{{ errorMessage() }}</p> }
      }
    </ain-page>
    <p-dialog [header]="t('sales.installments.pay')" [visible]="showPayDialog()" (visibleChange)="showPayDialog.set($event)" [modal]="true" [style]="{width:'360px'}">
      <label><span>{{ t('sales.installments.fields.amount') }}</span><p-inputNumber [(ngModel)]="payForm.amount" [min]="0" /></label>
      <ng-template pTemplate="footer">
        <button pButton type="button" [label]="t('common.save')" [disabled]="paying()" (click)="submitPay()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .card{border:1px solid #e5e7eb;border-radius:12px;padding:12px;margin-bottom:10px;background:#fff}
    .toolbar{display:flex;justify-content:space-between;align-items:center}
    .back-link{color:#1d4ed8;font-weight:600;text-decoration:none}
    .grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}
    label{display:grid;gap:6px}
    label.check{grid-template-columns:auto 1fr;align-items:center}
    label.full,.preview.full{grid-column:1/-1}
    .preview{margin:0;font-size:14px}
    .summary{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:12px;font-size:14px}
    .summary article{display:grid;gap:4px}
    .summary span{color:#6b7280;font-size:12px}
    .summary .overdue{color:#b91c1c}
    .tabs{display:flex;gap:8px;margin-bottom:10px}
    .tabs button{border:1px solid #cbd5e1;background:#fff;border-radius:999px;padding:6px 12px;cursor:pointer}
    .tabs button.active{background:#1d4ed8;color:#fff;border-color:#1d4ed8}
    .error{color:#b91c1c}
    @media (max-width:760px){.grid{grid-template-columns:1fr}}
  `]
})
export class SalesInstallmentPlanDetailComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly paying = signal(false);
  readonly isCreateMode = signal(false);
  readonly errorMessage = signal('');
  readonly activeTab = signal<'schedules' | 'payments'>('schedules');
  readonly schedules = signal<InstallmentScheduleRecord[]>([]);
  readonly payments = signal<InstallmentPaymentRecord[]>([]);
  readonly plan = signal<InstallmentPlanRecord | null>(null);
  readonly showPayDialog = signal(false);

  readonly totalPaid = computed(() =>
    this.schedules().reduce((sum, row) => sum + row.paidAmount, 0));
  readonly totalRemaining = computed(() =>
    this.schedules().reduce((sum, row) => sum + row.remainingAmount, 0));
  readonly totalInterest = computed(() => {
    const p = this.plan();
    if (!p) return 0;
    return Math.max(0, (p.totalAmount ?? 0) - this.planPrincipalAmount());
  });
  readonly overdueAmount = computed(() => {
    const now = Date.now();
    return this.schedules().reduce((sum, row) => {
      if (row.remainingAmount <= 0) return sum;
      const isOverdue = row.status === 'Overdue' || new Date(row.dueDateUtc).getTime() < now;
      return isOverdue ? sum + row.remainingAmount : sum;
    }, 0);
  });

  payTarget: InstallmentScheduleRecord | null = null;
  payForm = { amount: 0 };
  frequencyOptions = [
    { value: 'Monthly', labelKey: 'sales.installments.frequencies.monthly' },
    { value: 'Weekly', labelKey: 'sales.installments.frequencies.weekly' }
  ];
  feePeriodOptions = [
    { value: 'Annual', labelKey: 'sales.installments.feePeriods.annual' },
    { value: 'Monthly', labelKey: 'sales.installments.feePeriods.monthly' }
  ];
  form = {
    planNumber: '',
    customerAccountId: '' as string | null,
    totalAmount: 0,
    currencyCode: 'IRR',
    installmentCount: 3,
    frequency: 'Monthly',
    firstDueDateUtc: null as string | null,
    hasInterest: false,
    feeRate: 12,
    feePeriod: 'Annual'
  };

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('planId');
    this.isCreateMode.set(!id || id === 'new');
    if (this.isCreateMode()) { this.loading.set(false); return; }
    this.loadPlan(id!);
  }

  setTab(tab: 'schedules' | 'payments'): void {
    this.activeTab.set(tab);
    if (tab === 'payments') {
      const id = this.route.snapshot.paramMap.get('planId');
      if (id) this.loadPayments(id);
    }
  }

  previewTotalAmount(): number {
    const principal = this.form.totalAmount;
    if (principal <= 0 || this.form.installmentCount <= 0) return 0;
    if (!this.form.hasInterest || this.form.feeRate <= 0) return principal;
    const periodRate = this.resolvePeriodRate(this.form.feeRate, this.form.feePeriod, this.form.frequency);
    const payment = this.equalPayment(principal, periodRate, this.form.installmentCount);
    return Math.round(payment * this.form.installmentCount);
  }

  loadPlan(id: string): void {
    this.loading.set(true);
    this.api.getInstallmentPlan(id).subscribe({
      next: p => this.plan.set(p),
      error: () => this.errorMessage.set(this.t('sales.installments.errorLoad'))
    });
    this.api.listInstallmentSchedules(id).subscribe({
      next: rows => { this.schedules.set(rows); this.loading.set(false); },
      error: () => { this.errorMessage.set(this.t('sales.installments.errorLoad')); this.loading.set(false); }
    });
    this.loadPayments(id);
  }

  loadPayments(id: string): void {
    this.api.listInstallmentPayments(id).subscribe({
      next: rows => this.payments.set(rows),
      error: () => this.payments.set([])
    });
  }

  planPrincipalAmount(): number {
    const p = this.plan();
    if (!p) return 0;
    const principal = p.principalAmount ?? p.totalAmount ?? 0;
    return Number.isFinite(principal) ? principal : 0;
  }

  installmentSequence(scheduleId: string): number {
    return this.schedules().find(item => item.id === scheduleId)?.sequenceNumber ?? 0;
  }

  statusLabel(status: string): string {
    const key = `sales.installments.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  save(): void {
    if (!this.form.customerAccountId) { this.errorMessage.set(this.t('sales.credit.evaluate.customerRequired')); return; }
    if (this.form.hasInterest && this.form.feeRate <= 0) {
      this.errorMessage.set(this.t('sales.installments.feeRateRequired'));
      return;
    }
    this.saving.set(true);
    const payload: CreateInstallmentPlanPayload = {
      planNumber: this.form.planNumber,
      customerAccountId: this.form.customerAccountId,
      salesOrderId: null,
      totalAmount: this.form.totalAmount,
      hasInterest: this.form.hasInterest,
      feeRate: this.form.hasInterest ? this.form.feeRate : 0,
      feePeriod: this.form.hasInterest ? this.form.feePeriod : 'Monthly',
      currencyCode: this.form.currencyCode,
      installmentCount: this.form.installmentCount,
      frequency: this.form.frequency,
      firstDueDateUtc: this.form.firstDueDateUtc ?? new Date().toISOString(),
      notes: null
    };
    this.api.createInstallmentPlan(payload).subscribe({
      next: plan => void this.router.navigate(['/sales/installment-plans', plan.id]),
      error: () => { this.errorMessage.set(this.t('sales.installments.errorSave')); this.saving.set(false); }
    });
  }

  feePeriodLabel(value: string): string {
    const key = this.feePeriodOptions.find(item => item.value === value)?.labelKey;
    return key ? this.t(key) : value;
  }

  openPay(row: InstallmentScheduleRecord): void {
    this.payTarget = row;
    this.payForm.amount = Math.round(row.remainingAmount);
    this.showPayDialog.set(true);
  }

  submitPay(): void {
    const planId = this.route.snapshot.paramMap.get('planId')!;
    if (!this.payTarget) return;
    this.paying.set(true);
    this.api.recordInstallmentPayment(planId, {
      installmentScheduleId: this.payTarget.id,
      amount: this.payForm.amount,
      idempotencyKey: `pay-${Date.now()}`
    }).subscribe({
      next: () => {
        this.showPayDialog.set(false);
        this.paying.set(false);
        this.loadPlan(planId);
        if (this.activeTab() === 'payments') {
          this.loadPayments(planId);
        }
      },
      error: () => { this.errorMessage.set(this.t('sales.installments.payError')); this.paying.set(false); }
    });
  }

  private resolvePeriodRate(feeRatePercent: number, feePeriod: string, frequency: string): number {
    const annual = feeRatePercent / 100;
    if (feePeriod === 'Annual' && frequency === 'Monthly') return annual / 12;
    if (feePeriod === 'Monthly' && frequency === 'Monthly') return feeRatePercent / 100;
    if (feePeriod === 'Annual' && frequency === 'Weekly') return annual / 52;
    if (feePeriod === 'Monthly' && frequency === 'Weekly') return (feeRatePercent / 100) / 4.345;
    return feeRatePercent / 100;
  }

  private equalPayment(principal: number, periodRate: number, count: number): number {
    if (periodRate <= 0) return principal / count;
    const factor = Math.pow(1 + periodRate, count);
    return principal * periodRate * factor / (factor - 1);
  }

  t(key: string): string { return this.localization.translate(key); }
}
